"use client";

import { useAction, useQuery } from "convex/react";
import { useEffect, useRef, useState } from "react";
import { api } from "../../../convex/_generated/api";
import type { BillingCurrency } from "../../../convex/lib/billingRail";
import { isTrinidadCountry } from "../../../convex/lib/billingRail";
import { isStudioPreviewHost } from "@/studio/lib/studio-preview-host";
import {
  CHECKOUT_FX_WAIT_MS,
  countryCodeFromGeoHeader,
  regionFromLocale,
  checkoutDisplayRate,
} from "../../../convex/lib/checkoutCurrency";

function localeNow(): string | null {
  return typeof navigator === "undefined" ? null : navigator.language;
}

/** Preview only: ?country=TT or ?country=US overrides the edge country. */
function previewCountryOverride(): string | null {
  if (typeof window === "undefined") return null;
  if (!isStudioPreviewHost(window.location.hostname)) return null;
  return countryCodeFromGeoHeader(new URLSearchParams(window.location.search).get("country"));
}

/** Trinidad and Tobago from edge geo, then wallet country, then browser locale. */
export function checkoutIsTrinidad(input: {
  geoCountry?: string | null;
  walletCountry?: string | null;
  locale?: string | null;
}): boolean {
  const geo = countryCodeFromGeoHeader(input.geoCountry);
  if (geo) return geo === "TT";
  const wallet = (input.walletCountry ?? "").trim();
  if (wallet) return isTrinidadCountry(wallet);
  return regionFromLocale(input.locale) === "TT";
}

/**
 * Everyone (any country): USD (card or PayPal) by default, TTD (Wam) available.
 * Geo / ?country= is informational only and no longer gates options.
 */
export function useCheckoutCurrency(walletCountry?: string | null) {
  const touched = useRef(false);
  const [geoCountry, setGeoCountry] = useState<string | null>(null);
  const [currency, setCurrencyState] = useState<BillingCurrency>("USD");
  const cachedFx = useQuery(api.fx.getUsdToTtd);
  const refreshFx = useAction(api.fx.refreshUsdToTtd);
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const forced = previewCountryOverride();
    if (forced) {
      setGeoCountry(forced);
      return;
    }
    void fetch("/api/checkout/country")
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { country?: string | null } | null) => {
        if (cancelled || touched.current) return;
        const geo = body && typeof body.country === "string" ? body.country : null;
        setGeoCountry(geo);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [walletCountry]);

  useEffect(() => {
    if (cachedFx && !cachedFx.stale) return;
    void refreshFx({}).catch(() => {});
  }, [cachedFx, refreshFx]);

  useEffect(() => {
    if (cachedFx?.rate) return;
    const timer = window.setTimeout(() => setWaited(true), CHECKOUT_FX_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [cachedFx?.rate]);

  const ttdAllowed = true;

  const display = checkoutDisplayRate({ cached: cachedFx?.rate, waited });

  return {
    currency,
    geoCountry,
    ttdAllowed,
    fxUsdToTtd: display.rate,
    fxApproximate: display.approximate,
    setCurrency(next: BillingCurrency) {
      touched.current = true;
      setCurrencyState(next);
    },
  };
}

/** Signed-in email, then backup. Skip on the static checkout preview. */
export function useCheckoutContactEmail(skip = false): string {
  const me = useQuery(api.users.current, skip ? "skip" : {});
  return me?.email || me?.backupEmail || "";
}

/** "Send receipts to" options: Yatishara address and backup email. Skip on the static preview. */
export function useCheckoutReceiptEmails(skip = false): string[] {
  const me = useQuery(api.users.current, skip ? "skip" : {});
  return Array.from(
    new Set([me?.email, me?.backupEmail].filter((e): e is string => Boolean(e && e.trim()))),
  );
}
